import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

export type NetworkStatus = "online" | "offline" | "serverUnreachable";

interface UseNetworkStatusReturn {
  isOnline: boolean;
  networkStatus: NetworkStatus;
}

const API_URL = import.meta.env.VITE_API_URL;

const useHealthQuery = () =>
  useQuery({
    queryKey: ["healthz"],
    queryFn: async ({ signal }) => {
      const fetchPromise = fetch(`${API_URL}/healthz`, {
        method: "GET",
        cache: "no-cache",
        signal,
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        const id = setTimeout(() => reject(new Error("Server timeout")), 7000); //7s
        signal.addEventListener("abort", () => clearTimeout(id));
      });

      const response = await Promise.race([fetchPromise, timeoutPromise]);
      return response.ok;
    },
    enabled: navigator.onLine,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // This probe *is* the offline detector - it fails routinely whenever the
    // user or the server is unreachable, and the OfflineOverlay owns that
    // state. Even a 5xx here must not become a Sentry event.
    meta: { skipErrorReporting: true },
  });

/**
 * Custom hook to monitor network connectivity status
 *
 * Monitors browser online/offline events and attempts to distinguish between:
 * - No internet connection (offline)
 * - Internet available but server unreachable (server-unreachable)
 * - Fully online and connected (online)
 *
 * Uses the backend /healthz endpoint to verify server connectivity.
 * Uses React Query for automatic request cancellation and lifecycle management.
 *
 * @returns {UseNetworkStatusReturn} Current network status
 */
export const useNetworkStatus = (): UseNetworkStatusReturn => {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const { data, refetch, isFetched } = useHealthQuery();

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(true);
      refetch();
    };

    const updateOfflineStatus = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOfflineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOfflineStatus);
    };
  }, [refetch]);

  const failedToFetchHealth = isFetched && !data;
  const networkStatus: NetworkStatus = isOnline
    ? failedToFetchHealth
      ? "serverUnreachable"
      : "online"
    : "offline";

  return { isOnline, networkStatus };
};
